import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, Input, message, Modal } from "antd";
import { useCreateCooperative } from "../../api/address";
import { AlertCircle, Info, Loader2, MapPin } from "lucide-react";
import { AppButton } from "../ui/AppButton";

const cooperativeSchema = z.object({
  name: z
    .string()
    .min(1, "Cooperative name is required")
    .regex(/^[A-Za-z\s]+$/, "Cooperative name must contain only letters"),
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  shortName: z
    .string()
    .min(1, "Short name is required")
    .max(10, "Short name must be 10 characters or less"),
  phoneNumber: z.string().min(1, "Phone number is required"),
  address: z
    .object({
      street: z.string().min(1, "Street address is required"),
      city: z.string().min(1, "City is required"),
      country: z.string().min(1, "Country is required"),
    })
    .required(),
});

type CooperativeFormData = z.infer<typeof cooperativeSchema>;

interface CooperativeFormProps {
  unionId?: number | undefined;
  title: string;
  open: boolean;
  onCancel?: () => void;
  onOk?: () => void;
  confirmLoading: boolean;
}

const CooperativeForm = ({ unionId, open, onCancel }: CooperativeFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CooperativeFormData>({
    resolver: zodResolver(cooperativeSchema),
    defaultValues: {
      name: "",
      email: "",
      shortName: "",
      phoneNumber: "",
      address: {
        street: "",
        city: "",
        country: "",
      },
    },
  });

  const { mutate: createCooperative, isPending: isCreatingCooperative } =
    useCreateCooperative(unionId || 0);
  const onSubmit = async (data: CooperativeFormData) => {
    const payload = {
      unionId,
      name: data.name,
      email: data.email,
      shortName: data.shortName,
      phoneNumber: data.phoneNumber,
      type: "MULTI-PURPOSE-COOPERATIVE",
    };

    try {
      createCooperative(payload, {
        onSuccess: () => {
          message.success("Cooperative created successfully");
          reset();
          onCancel?.();
        },
        onError: (error) => {
          console.error("Error creating cooperative:", error);
          message.error("Failed to create cooperative");
        },
      });
    } catch (err) {
      console.error("Unexpected error:", err);
      message.error("An unexpected error occurred");
    }
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  return (
    <Modal
      title=""
      open={open}
      footer={null}
      onCancel={handleCancel}
      afterClose={() => reset()}
      width={800}
    >
      <Form onFinish={handleSubmit(onSubmit)}>
        <div className="p-1">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Add Cooperative
            </h2>
            <p className="text-gray-500 mt-1">
              Fill in the cooperative details below
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Cooperative Name */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Cooperative Name <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter cooperative name"
                      className={`w-full transition-all duration-200 ${
                        errors.name
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-lg shadow-sm`}
                    />
                  )}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Short Name */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Short Name <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="shortName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter short name (max 10 characters)"
                      className={`w-full transition-all duration-200 ${
                        errors.shortName
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-lg shadow-sm`}
                    />
                  )}
                />
                {errors.shortName && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.shortName.message}
                  </p>
                )}
              </div>

              {/* Address Section */}
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Address Information <span className="text-red-500">*</span>
                </label>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-600">
                      Street
                    </label>
                    <Controller
                      name="address.street"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="Enter street address"
                          className={`w-full transition-all duration-200 ${
                            errors.address?.street
                              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                              : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                          } rounded-lg shadow-sm`}
                        />
                      )}
                    />
                    {errors.address?.street && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.address.street.message}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600">
                        City
                      </label>
                      <Controller
                        name="address.city"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Enter city"
                            className={`w-full transition-all duration-200 ${
                              errors.address?.city
                                ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                                : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            } rounded-lg shadow-sm`}
                          />
                        )}
                      />
                      {errors.address?.city && (
                        <p className="text-red-500 text-xs flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.address.city.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600">
                        Country
                      </label>
                      <Controller
                        name="address.country"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Enter country"
                            className={`w-full transition-all duration-200 ${
                              errors.address?.country
                                ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                                : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            } rounded-lg shadow-sm`}
                          />
                        )}
                      />
                      {errors.address?.country && (
                        <p className="text-red-500 text-xs flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.address.country.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Email */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="email"
                      placeholder="Enter email address"
                      className={`w-full transition-all duration-200 ${
                        errors.email
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-lg shadow-sm`}
                    />
                  )}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Enter phone number"
                      className={`w-full transition-all duration-200 ${
                        errors.phoneNumber
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                      } rounded-lg shadow-sm`}
                    />
                  )}
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.phoneNumber.message}
                  </p>
                )}
              </div>

              {/* Info Card */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">
                      Quick Tips
                    </h4>
                    <p className="text-xs text-blue-700 mt-1">
                      Ensure all required fields (marked with *) are filled
                      accurately. The short name should be memorable and under
                      10 characters.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <AppButton
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="px-6 py-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              Cancel
            </AppButton>
            <AppButton
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
              disabled={isSubmitting || isCreatingCooperative}
            >
              {isSubmitting || isCreatingCooperative ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </div>
              ) : (
                "Create Cooperative"
              )}
            </AppButton>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default CooperativeForm;
